/**
 * The provider tree, in one place so it can be mounted identically by
 * `main.tsx` and by the wiring test.
 *
 * That is not tidiness. The whole dashboard shipped unreachable once because
 * `main.tsx` was the only file that composed the app and nothing rendered it:
 * every component test mounted its subject directly and passed. Keeping the
 * composition here means `App.test.tsx` exercises the real thing.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { AuthProvider } from './auth/useAuth'
import { I18nProvider } from './i18n'

/**
 * Per-window polling lives on the individual queries (`WINDOW_REFRESH_MS`),
 * because 30m data turns over every minute and 7d data every hour. What is
 * global is the retry policy: the API client already handles a 401 by
 * refreshing once and retrying, so a 401 that reaches this layer means the
 * session is genuinely over and retrying cannot help.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) =>
          !(error instanceof Error && error.name === 'ApiError') && failureCount < 2,
      },
    },
  })
}

export function AppProviders({ client, children }: { client: QueryClient; children: ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      {/* Above AuthProvider because the sign-in and session-expired screens are
          text too: everything that can render a string has to be inside it.
          It fetches nothing and holds one enum, so it costs the tree nothing. */}
      <I18nProvider>
        {/* AuthProvider is what registers the session-expired handler with the
            API client, so it has to wrap everything that can make a request. */}
        <AuthProvider>{children}</AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  )
}
